import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/home', icon: 'Home' },
    { label: 'Services', path: '/services', icon: 'Settings' },
    { label: 'Portfolio', path: '/portfolio', icon: 'Briefcase' },
    { label: 'Blog', path: '/blog', icon: 'FileText' },
    { label: 'Contact', path: '/contact', icon: 'Mail' }
  ];

  const isActivePath = (path) => location.pathname === path;

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) setSearchQuery('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log('Search query:', searchQuery);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => (document.body.style.overflow = 'unset');
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-1000">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14"> {/* smaller header height */}

            {/* Mobile Menu Button on Left */}
            <div className="flex md:hidden items-center mr-2">
              <button
                onClick={toggleMobileMenu}
                className="p-2 text-text-secondary hover:text-text-primary focus-ring rounded-md"
              >
                <Icon name="Menu" size={20} />
              </button>
            </div>

            {/* Logo */}
            <div className="flex-shrink-0 flex-1">
              <Link to="/home" onClick={closeMobileMenu} className="flex items-center justify-center md:justify-start">
                <img src="/zentirx_logo.png" className="h-8 w-auto select-none" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6 h-full">
              {navigationItems.map((item) => {
                const active = isActivePath(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`group relative flex items-center h-14 px-1 text-sm font-medium transition-colors duration-200 ${active ? 'text-[#1e40af]' : 'text-text-secondary hover:text-[#1e40af]'
                      }`}
                  >
                    {item.label}
                    <span
                      className={`absolute bottom-2 left-1/2 -translate-x-1/2 bg-[#1e40af] transition-all duration-300 ease-out ${active
                          ? 'w-full h-[3px]' 
                          : 'w-0 h-[3px] group-hover:w-full' 
                        }`}
                    />
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Search */}
            <div className="hidden md:flex items-center relative ml-4">
              {isSearchOpen && (
                <form onSubmit={handleSearchSubmit} className="absolute right-0 top-1/2 -translate-y-1/2 z-50">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-64 pl-4 pr-10 py-2 border border-border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={toggleSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                </form>
              )}

              <button
                onClick={toggleSearch}
                className="p-2 text-text-secondary hover:text-text-primary nav-transition focus-ring rounded-md"
              >
                <Icon name="Search" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {isSearchOpen && (
          <div className="md:hidden border-t border-border bg-surface px-4 py-3">
            <form onSubmit={handleSearchSubmit}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={toggleSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-text-secondary hover:text-text-primary nav-transition"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </form>
          </div>
        )}
      </header>

      {/* Mobile Navigation Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-1050 md:hidden"
            onClick={closeMobileMenu}
          />

          {/* Drawer */}
          <div className="fixed top-0 left-0 h-full w-[80vw] max-w-xs bg-surface shadow-elevation-3 z-1100 md:hidden animate-slide-in">
            <div className="flex flex-col h-full">

              {/* Drawer Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <Link to="/home" onClick={closeMobileMenu} className="flex items-center space-x-3">
                  <img src="/zentirx_logo.png" className="h-8 w-auto select-none" />
                </Link>
                <button
                  onClick={closeMobileMenu}
                  className="p-2 text-text-secondary hover:text-text-primary nav-transition focus-ring rounded-md"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {/* Navigation Items */}
              <nav className="flex-1 px-6 py-6">
                <div className="space-y-2">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-md nav-transition focus-ring ${isActivePath(item.path)
                          ? 'bg-primary-50 text-primary border-l-4 border-primary'
                          : 'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
                        }`}
                    >
                      <Icon name={item.icon} size={20} />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </nav>

              {/* Mobile Search Button */}
              <div className="px-6 py-4 border-t border-border">
                <button
                  onClick={() => {
                    setIsSearchOpen(true);
                    closeMobileMenu();
                  }}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-md nav-transition focus-ring"
                >
                  <Icon name="Search" size={20} />
                  <span className="font-medium">Search Blog</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
